import React, { useState, useEffect } from 'react';
import { fetchItems, updateItem as apiUpdateItem, deleteItem as apiDeleteItem } from '../config/api';
import { useStore } from '../context/StoreContext';
import { 
  Edit2, 
  Trash2, 
  DollarSign, 
  Tag, 
  FileText, 
  ImagePlus,
  Package,
  Save,
  X
} from 'lucide-react';

function ItemList({ selectedCategory }) {
  const { items, setInitialItems, updateItem, deleteItem } = useStore();
  const [filteredItems, setFilteredItems] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({
    name: '',
    label: '',
    description: '',
    price: '',
    category: '',
  });
  const [loading, setLoading] = useState(false);
  const [editImage, setEditImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadItems();
  }, []);

  useEffect(() => {
    if (selectedCategory && selectedCategory !== '') {
      const cleanSelectedCategory = selectedCategory.replace(/^ObjectId\(["'](.+)["']\)$/, '$1');
      const filtered = items.filter((item) => {
        const itemCategory = typeof item.category === 'string' 
          ? item.category.replace(/^ObjectId\(["'](.+)["']\)$/, '$1')
          : item.category?._id || item.category;
        return itemCategory === cleanSelectedCategory;
      });
      setFilteredItems(filtered);
    } else {
      setFilteredItems(items);
    }
  }, [selectedCategory, items]);

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchItems();
      setInitialItems(data);
    } catch (error) {
      setError('Failed to load items. Please try again.');
      console.error('Failed to load items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (item) => {
    setEditId(item._id);
    setEditData({
      name: item.name,
      label: item.label,
      description: item.description,
      price: item.price,
      category: item.category._id || item.category,
    });
    setImagePreview(item.imageUrl || `${process.env.REACT_APP_API_BASEURL}/${item.image}`);
    setError(null);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }
      
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setError('Only JPEG, PNG, and WebP images are allowed');
        return;
      }

      setEditImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      setError(null);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      Object.keys(editData).forEach((key) => {
        if (editData[key] !== '') {
          formData.append(key, editData[key]);
        }
      });

      if (editImage) {
        formData.append('image', editImage);
      }

      const updatedItem = await apiUpdateItem(editId, formData);
      updateItem(editId, updatedItem);

      // Reset form state
      setEditId(null);
      setEditData({
        name: '',
        label: '',
        description: '',
        price: '',
        category: '',
      });
      setEditImage(null);
      setImagePreview(null);

    } catch (error) {
      setError('Failed to update item. Please try again.');
      console.error('Error updating item:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      setLoading(true);
      setError(null);
      try {
        await apiDeleteItem(id);
        deleteItem(id);
      } catch (error) {
        setError('Failed to delete item. Please try again.');
        console.error('Error deleting item:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const resetForm = () => {
    setEditId(null);
    setEditData({
      name: '',
      label: '',
      description: '',
      price: '',
      category: '',
    });
    setEditImage(null);
    setImagePreview(null);
    setError(null);
  };

  if (loading && !items.length) {
    return (
      <div className="flex items-center justify-center p-6">
        <div className="text-amber-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-md rounded-xl">
      <div className="p-6">
        <h2 className="flex items-center mb-6 text-xl font-semibold text-amber-900">
          <Package className="w-6 h-6 mr-2" />
          Menu Items
        </h2>

        {error && (
          <div className="p-4 mb-4 text-red-700 bg-red-100 rounded-lg">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filteredItems.length > 0 ? (
            filteredItems.map((item) => (
              <div
                key={item._id}
                className="overflow-hidden transition-transform bg-white shadow-md rounded-xl hover:shadow-lg"
              >
                {editId === item._id ? (
                  <form onSubmit={handleEditSubmit} className="p-4">
                    <div className="space-y-4">
                      <div>
                        <label className="flex items-center mb-1 text-sm font-medium text-gray-700">
                          <Tag className="w-4 h-4 mr-1" />
                          Name
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={editData.name}
                          onChange={handleEditChange}
                          className="w-full px-3 py-2 border rounded-lg border-amber-200 focus:ring-2 focus:ring-amber-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="flex items-center mb-1 text-sm font-medium text-gray-700">
                          <Tag className="w-4 h-4 mr-1" />
                          Label
                        </label>
                        <input
                          type="text"
                          name="label"
                          value={editData.label}
                          onChange={handleEditChange}
                          className="w-full px-3 py-2 border rounded-lg border-amber-200 focus:ring-2 focus:ring-amber-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="flex items-center mb-1 text-sm font-medium text-gray-700">
                          <FileText className="w-4 h-4 mr-1" />
                          Description
                        </label>
                        <textarea
                          name="description"
                          value={editData.description}
                          onChange={handleEditChange}
                          rows="3"
                          className="w-full px-3 py-2 border rounded-lg border-amber-200 focus:ring-2 focus:ring-amber-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="flex items-center mb-1 text-sm font-medium text-gray-700">
                          <DollarSign className="w-4 h-4 mr-1" />
                          Price
                        </label>
                        <input
                          type="number"
                          name="price"
                          value={editData.price}
                          onChange={handleEditChange}
                          step="0.01"
                          min="0"
                          className="w-full px-3 py-2 border rounded-lg border-amber-200 focus:ring-2 focus:ring-amber-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="flex items-center mb-1 text-sm font-medium text-gray-700">
                          <ImagePlus className="w-4 h-4 mr-1" />
                          Image
                        </label>
                        <div className="flex justify-center px-6 pt-5 pb-6 mt-1 border-2 border-dashed rounded-lg border-amber-200">
                          <div className="space-y-1 text-center">
                            {imagePreview && (
                              <img
                                src={imagePreview}
                                alt="Preview"
                                className="object-cover w-32 h-32 mx-auto mb-4 rounded-lg"
                              />
                            )}
                            <div className="flex text-sm text-gray-600">
                              <label className="relative font-medium bg-white rounded-md cursor-pointer text-amber-600 hover:text-amber-500">
                                <span>Change image</span>
                                <input
                                  type="file"
                                  className="sr-only"
                                  onChange={handleImageChange}
                                  accept="image/jpeg,image/png,image/webp"
                                />
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex space-x-2">
                        <button
                          type="submit"
                          disabled={loading}
                          className="inline-flex items-center justify-center flex-1 px-4 py-2 text-white border border-transparent rounded-lg shadow-sm bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
                        >
                          <Save className="w-4 h-4 mr-2" />
                          {loading ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          type="button"
                          onClick={resetForm}
                          className="inline-flex items-center justify-center flex-1 px-4 py-2 text-white bg-gray-600 border border-transparent rounded-lg shadow-sm hover:bg-gray-700"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Cancel
                        </button>
                      </div>
                    </div>
                  </form>
                ) : (
                  <>
                    {item.image && (
                      <div className="relative h-48 bg-gray-200">
                        <img
                          src={item.imageUrl || `http://localhost:3001/${item.image}`}
                          alt={item.label}
                          className="object-cover w-full h-full"
                        />
                      </div>
                    )}
                    <div className="p-4">
                      <h3 className="mb-2 text-lg font-semibold text-gray-900">{item.label}</h3>
                      <p className="mb-3 text-sm text-gray-600">{item.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-bold text-amber-600">
                          ${typeof item.price === 'number' ? item.price.toFixed(2) : item.price}
                        </span>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditClick(item)}
                            className="p-2 transition-colors text-amber-600 hover:text-amber-700"
                          >
                            <Edit2 className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(item._id)}
                            className="p-2 text-red-600 transition-colors hover:text-red-700"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))
          ) : (
            <div className="py-8 text-center col-span-full">
              <Package className="w-12 h-12 mx-auto text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No items</h3>
              <p className="mt-1 text-sm text-gray-500">
                No items available for the selected category.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ItemList;